import type {
  LockerverseBooleanCustomField,
  LockerverseCatalog,
  LockerverseCheckoutItem,
  LockerverseCustomField,
  LockerverseCustomFieldAnswer,
  LockerverseMultiOptionCustomField,
  LockerverseSingleOptionCustomField,
} from "@lockerverse/sdk/checkout";
import { useId } from "react";
import {
  array,
  minLength,
  nonEmpty,
  pipe,
  safeParse,
  string,
  trim,
} from "valibot";
import { Checkbox, FieldError, FormField, TextField } from "./form-field.tsx";

export type CustomFieldValues = Record<string, boolean | string | string[]>;

export function getCheckoutCustomFields(
  catalog: LockerverseCatalog,
  items: readonly LockerverseCheckoutItem[]
) {
  const selectedOptionIds = new Set(items.map((item) => item.productId));
  return catalog.widget.customFields.filter(
    (field) =>
      !field.paymentOptionIds?.length ||
      field.paymentOptionIds.some((id) => selectedOptionIds.has(id))
  );
}

/** Normalize initial values against configured fields; unknown fields and options are discarded. */
export function createCustomFieldValues(
  fields: LockerverseCustomField[],
  initialValues: CustomFieldValues = {}
): CustomFieldValues {
  return Object.fromEntries(
    fields.map((field) => [
      field.id,
      reconcileCustomFieldValue(
        field,
        Object.hasOwn(initialValues, field.id)
          ? initialValues[field.id]
          : undefined
      ),
    ])
  );
}

export function reconcileCustomFieldValues(
  fields: LockerverseCustomField[],
  values: CustomFieldValues
) {
  const next: CustomFieldValues = {};
  for (const field of fields) {
    const value = values[field.id];
    next[field.id] = reconcileCustomFieldValue(field, value);
  }
  const oldKeys = Object.keys(values);
  if (
    oldKeys.length === fields.length &&
    fields.every((field) => {
      const before = values[field.id];
      const after = next[field.id];
      return (
        Object.hasOwn(values, field.id) && sameCustomFieldValue(before, after)
      );
    })
  ) {
    return values;
  }
  return next;
}

function reconcileCustomFieldValue(
  field: LockerverseCustomField,
  value: CustomFieldValues[string] | undefined
) {
  if (field.type === "checkbox") {
    return value === true;
  }
  if (field.type === "checkboxes" || field.type === "multi_select") {
    const optionIds = new Set(field.options.map((option) => option.id));
    return Array.isArray(value) ? value.filter((id) => optionIds.has(id)) : [];
  }
  const text = getStringValue(value);
  if (field.type === "radio" || field.type === "select") {
    return field.options.some((option) => option.id === text) ? text : "";
  }
  return text;
}

function sameCustomFieldValue(
  before: CustomFieldValues[string] | undefined,
  after: CustomFieldValues[string] | undefined
) {
  if (Array.isArray(before) && Array.isArray(after)) {
    return (
      before.length === after.length &&
      before.every((id, index) => id === after[index])
    );
  }
  return before === after;
}

function getStringValue(value: CustomFieldValues[string] | undefined) {
  const result = safeParse(string(), value);
  return result.success ? result.output : "";
}

export function buildCustomFieldAnswers(
  fields: LockerverseCustomField[],
  values: CustomFieldValues
): LockerverseCustomFieldAnswer[] {
  return fields.map((field): LockerverseCustomFieldAnswer => {
    const value = values[field.id];
    if (field.type === "checkbox") {
      return { fieldId: field.id, value: value === true };
    }
    if (field.type === "checkboxes" || field.type === "multi_select") {
      return { fieldId: field.id, value: Array.isArray(value) ? value : [] };
    }
    return {
      fieldId: field.id,
      value: getStringValue(value),
    };
  });
}

export function validateCustomFields(
  fields: LockerverseCustomField[],
  values: CustomFieldValues
) {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (!field.required) {
      continue;
    }
    const message = `${field.label} is required.`;
    const value = values[field.id];
    if (field.type === "checkbox") {
      if (value !== true) {
        errors[field.id] = message;
      }
      continue;
    }
    const result =
      field.type === "checkboxes" || field.type === "multi_select"
        ? safeParse(pipe(array(string()), minLength(1, message)), value ?? [])
        : safeParse(
            pipe(string(), trim(), nonEmpty(message)),
            getStringValue(value)
          );
    if (!result.success) {
      errors[field.id] = result.issues[0]?.message ?? message;
    }
  }
  return errors;
}

export function CustomFields({
  choiceClassName = "lockerverse-payment__check-row",
  errorClassName = "lockerverse-payment__field-message",
  errors = {},
  fields,
  idPrefix,
  onChange,
  values,
}: {
  choiceClassName?: string;
  errorClassName?: string;
  errors?: Record<string, string>;
  fields: LockerverseCustomField[];
  idPrefix?: string | undefined;
  onChange: (values: CustomFieldValues) => void;
  values: CustomFieldValues;
}) {
  return fields.map((field) => (
    <CustomField
      choiceClassName={choiceClassName}
      error={errors[field.id]}
      errorClassName={errorClassName}
      field={field}
      idPrefix={idPrefix}
      key={field.id}
      onChange={(value) => onChange({ ...values, [field.id]: value })}
      value={values[field.id]}
    />
  ));
}

export type CustomFieldProps = {
  choiceClassName?: string;
  error?: string | undefined;
  errorClassName?: string;
  field: LockerverseCustomField;
  idPrefix?: string | undefined;
  onChange: (value: boolean | string | string[]) => void;
  value: boolean | string | string[] | undefined;
};

export function CustomField({
  choiceClassName = "lockerverse-payment__check-row",
  errorClassName = "lockerverse-payment__field-message",
  ...props
}: CustomFieldProps) {
  const generatedId = useId();
  const { field, idPrefix = `lockerverse-${generatedId}` } = props;
  const controlId = `${idPrefix}-custom-${field.id}`;
  const errorId = `${controlId}-error`;
  const controlProps = {
    ...props,
    choiceClassName,
    controlId,
    errorClassName,
    errorId,
  };
  switch (field.type) {
    case "checkbox":
      return <BooleanCustomField {...controlProps} field={field} />;
    case "text":
    case "textarea":
      return <TextCustomField {...controlProps} field={field} />;
    case "select":
    case "multi_select":
      return <SelectCustomField {...controlProps} field={field} />;
    case "radio":
    case "checkboxes":
      return <ChoiceCustomField {...controlProps} field={field} />;
    default:
      return null;
  }
}

function BooleanCustomField({
  choiceClassName,
  controlId,
  error,
  errorClassName,
  errorId,
  field,
  onChange,
  value,
}: ControlProps & { field: LockerverseBooleanCustomField }) {
  return (
    <label className={choiceClassName} htmlFor={controlId}>
      <Checkbox
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? "true" : undefined}
        checked={value === true}
        id={controlId}
        name={`custom:${field.id}`}
        onChange={(event) => onChange(event.currentTarget.checked)}
        required={field.required}
      />
      <span>{field.label}</span>
      <FieldError className={errorClassName} error={error} id={errorId} />
    </label>
  );
}

type ControlProps = CustomFieldProps & {
  choiceClassName: string;
  errorClassName: string;
  controlId: string;
  errorId: string;
};

function TextCustomField({
  controlId,
  error,
  errorId,
  field,
  onChange,
  value,
}: ControlProps & {
  field: Extract<LockerverseCustomField, { type: "text" | "textarea" }>;
}) {
  const commonProps = {
    "aria-describedby": error ? errorId : undefined,
    "aria-invalid": error ? ("true" as const) : undefined,
    id: controlId,
    name: `custom:${field.id}`,
    onChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => onChange(event.currentTarget.value),
    required: field.required,
    value: getStringValue(value),
  };
  return field.type === "textarea" ? (
    <FormField
      error={error}
      errorId={errorId}
      id={controlId}
      label={field.label}
    >
      <textarea {...commonProps} className="lockerverse-form__control" />
    </FormField>
  ) : (
    <TextField
      {...commonProps}
      error={error}
      errorId={errorId}
      label={field.label}
    />
  );
}

function SelectCustomField({
  controlId,
  error,
  errorId,
  field,
  onChange,
  value,
}: ControlProps & {
  field: LockerverseMultiOptionCustomField | LockerverseSingleOptionCustomField;
}) {
  return (
    <FormField
      error={error}
      errorId={errorId}
      id={controlId}
      label={field.label}
    >
      <select
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? "true" : undefined}
        className="lockerverse-form__control"
        id={controlId}
        multiple={field.type === "multi_select"}
        name={`custom:${field.id}`}
        onChange={(event) =>
          onChange(
            field.type === "multi_select"
              ? [...event.currentTarget.selectedOptions].map(
                  (option) => option.value
                )
              : event.currentTarget.value
          )
        }
        required={field.required}
        value={
          value === true || value === false
            ? ""
            : (value ?? (field.type === "multi_select" ? [] : ""))
        }
      >
        {field.type === "select" ? <option value="">Select…</option> : null}
        {field.options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </FormField>
  );
}

function ChoiceCustomField({
  choiceClassName,
  controlId,
  error,
  errorClassName,
  errorId,
  field,
  onChange,
  value,
}: ControlProps & {
  field: LockerverseMultiOptionCustomField | LockerverseSingleOptionCustomField;
}) {
  const selectedValues = Array.isArray(value) ? value : [];
  return (
    <fieldset
      aria-describedby={error ? errorId : undefined}
      aria-invalid={error ? "true" : undefined}
    >
      <legend>{field.label}</legend>
      {field.options.map((option, index) => (
        <label className={choiceClassName} key={option.id}>
          <input
            checked={
              Array.isArray(value)
                ? value.includes(option.id)
                : value === option.id
            }
            className={
              field.type === "checkboxes"
                ? "lockerverse-form__checkbox"
                : undefined
            }
            id={`${controlId}-${index}`}
            name={`custom:${field.id}`}
            onChange={(event) => {
              if (field.type === "radio") {
                onChange(option.id);
                return;
              }
              onChange(
                event.currentTarget.checked
                  ? [...selectedValues, option.id]
                  : selectedValues.filter((id) => id !== option.id)
              );
            }}
            required={field.required && field.type === "radio"}
            type={field.type === "radio" ? "radio" : "checkbox"}
            value={option.id}
          />
          <span>{option.label}</span>
        </label>
      ))}
      <FieldError className={errorClassName} error={error} id={errorId} />
    </fieldset>
  );
}
